// @flow
import * as React from 'react';
import { withRouter } from 'react-router';
import deepEqual from 'react-fast-compare';
import queryString from 'query-string';
import { trackClicked } from 'utils/analytics';
import { connect } from 'react-redux';
import type { Location, RouterHistory } from 'react-router';
import { truncateToUTF8Bytes } from 'utils/string';
import { listingSearchHome } from 'utils/searchHomePages';
import { stringify } from 'utils/functional';
import { getParam } from 'utils/url';
import type { DisplayedType } from 'utils/constants';
import { listingTypesBySection } from 'utils/constants';
import SearchInputs from 'components/SearchInputs';
import type { SearchSection } from 'api/shared';
import type { State as MainSearch } from 'redux/main/reducers/mainSearch';
import type { CurrentUser } from 'api/currentUser';
import { REMOTE_KEYWORDS } from 'utils/constants/search';
import extractKeywordsFromQuery from 'utils/search/extractKeywordsFromQuery';
import { isEnabled as daysEnabled } from 'utils/idealistdayevents';
import {
  initializeSearch as initializeSearchAction,
  initializeLandingPageSearch as initializeLandingPageSearchAction,
  setUserInitiatedSearch as setUserInitiatedSearchAction,
  setSearchFilter as setSearchFilterAction,
  setSearchQuery as setSearchQueryAction,
  setQueryKeywords as setQueryKeywordsAction,
  setPageIndex as setPageIndexAction
} from '../redux/main/actions/mainSearch';
import {
  updateSearchLocation as updateSearchLocationAction,
  clearSearchLocation as clearSearchLocationAction
} from '../redux/main/actions/currentUser';
import { showModal as showModalAction } from '../redux/main/actions/modal';

const { useEffect, useState, useCallback } = React;

type OwnProps = {
  onSearch?: Object => void,
  searchSection: SearchSection,
  inPageHeader?: boolean,
  location: Location,
  history: RouterHistory
};

type Props = {
  ...$Exact<OwnProps>,
  searchFrom?: string,
  searchMode?: boolean,
  searchSection: SearchSection,
  type: ListingType,
  query: string,
  initialized: boolean,
  currentUser: CurrentUser,
  mainSearch: ?MainSearch,
  setUserInitiatedSearch: () => mixed,
  setPageIndex: (number, boolean) => mixed,
  setSearchQuery: (string, boolean) => mixed,
  setQueryKeywords: (Array<string>) => mixed,
  setSearchFilter: (string, mixed) => mixed,
  updateSearchLocation: (SearchLocation, boolean) => Promise<mixed>,
  clearSearchLocation: boolean => Promise<mixed>,
  initializeLandingPageSearch: Function,
  initializeSearch: Function,
  defaultSearchType: ?ListingType,
  searchMode: boolean,
  showModal: Function
};

const SearchInputsContainer = (props: Props) => {
  const [listingTypes, setListingTypes] = useState<Array<DisplayedType>>(
    listingTypesBySection[props.searchSection]
  );

  useEffect(
    () => {
      if (daysEnabled() && props.searchSection === 'MAIN') {
        setListingTypes(
          listingTypesBySection[props.searchSection].concat([
            'IDEALIST_DAY_EVENT'
          ])
        );
      }
    },
    [props.searchSection]
  );
  const [type, setType] = useState<ListingType>(
    props.type || props.defaultSearchType || listingTypes[0]
  );
  const [query, setQuery] = useState(
    truncateToUTF8Bytes(
      props.query || getParam(props.location, 'q', true) || '',
      512
    )
  );
  const [enqueueSearch, setEnqueueSearch] = useState([false, null]);
  const [location, setLocation] = useState<?SearchLocation>(
    props.currentUser.searchLocation
  );

  useEffect(
    () => {
      if (props.defaultSearchType) {
        setType(props.defaultSearchType);
      }
    },
    [props.defaultSearchType]
  );
  useEffect(
    () => {
      setLocation(props.currentUser.searchLocation);
    },
    [props.currentUser.searchLocation]
  );

  const setListingType = setType;
  const setUserQuery = useCallback(
    (q: string) => {
      // Algolia has a max query length of 512 bytes
      setQuery(truncateToUTF8Bytes(q, 512));
    },
    [setQuery]
  );
  const setSearchLocation = (
    searchLocation: ?SearchLocation,
    shouldEnqueueSearch?: boolean = true,
    setRemote?: boolean = false
  ) => {
    setLocation(searchLocation);
    if (setRemote) {
      props.setSearchFilter('listingLocationTypeFacet', [
        'REMOTE',
        'REMOTE_TEMPORARY_COVID'
      ]);
    }
    if (shouldEnqueueSearch) {
      setEnqueueSearch([true, query]);
    }
  };

  const search = useCallback(
    stateQuery => {
      const {
        clearSearchLocation,
        updateSearchLocation,
        setUserInitiatedSearch,
        history,
        initialized,
        searchFrom,
        searchMode,
        setSearchFilter,
        setSearchQuery,
        setQueryKeywords
      } = props;
      const stateSearchLocation = location;
      const stateType = type;

      if (!deepEqual(props.currentUser.searchLocation, stateSearchLocation)) {
        if (stateSearchLocation) {
          updateSearchLocation(stateSearchLocation, true).then(() =>
            props.setPageIndex(0, false)
          );
        } else {
          clearSearchLocation(true).then(() => props.setPageIndex(0, false));
        }
      }
      if (props.type && stateType !== props.type) {
        setSearchFilter('type', stateType);
      }

      let finalQuery = stateQuery;
      if (stateQuery !== props.query) {
        const [keywords, parsedQuery] = extractKeywordsFromQuery(
          stateQuery,
          REMOTE_KEYWORDS[USER_LOCALE]
        );
        if (keywords.length > 0 && initialized) {
          props.setSearchFilter('listingLocationTypeFacet', [
            'REMOTE',
            'REMOTE_TEMPORARY_COVID'
          ]);
        }
        setSearchQuery(parsedQuery, true);
        setQueryKeywords(keywords);
        finalQuery = parsedQuery;
      }

      const searchHome = listingSearchHome(stateType);
      if (searchHome && !searchMode) {
        if (finalQuery && finalQuery === props.query) {
          history.replace(`${searchHome}?q=${finalQuery}&searchMode=true`, {
            searchFrom
          });
        } else {
          history.push(`${searchHome}?q=${finalQuery}&searchMode=true`, {
            searchFrom
          });
        }
        setUserInitiatedSearch();
      }
    },
    [props, location, type]
  );

  const redirectTo = props.location
    ? `${props.location.pathname}${props.location.search}`
    : '';

  const clickLogin = (e: SyntheticEvent<>) => {
    e.preventDefault();
    trackClicked('Log In Search Hero');
    props.showModal('LOGIN', {
      redirectTo,
      manualLogin: true
    });
  };

  const clickSignup = (e: SyntheticEvent<>) => {
    e.preventDefault();
    trackClicked('Sign Up Search Hero');
    props.showModal('SIGNUP', {
      redirectTo
    });
  };

  const redirectParam = queryString.stringify({ to: redirectTo });

  useEffect(
    () => {
      const [enqueued, searchQuery] = enqueueSearch;
      if (enqueued) {
        setEnqueueSearch([false, null]);
        search(stringify(searchQuery));
      }
    },
    [enqueueSearch, props, search]
  );

  const clearSearchInput = useCallback(
    () => {
      setUserQuery('');
      if (props.searchMode) {
        search('');
      }
    },
    [setUserQuery, search, props.searchMode]
  );

  const selectedType =
    listingTypes.find(l => l === type) ||
    (listingTypes.includes('JOB') ? 'JOB' : listingTypes[0]);
  return (
    <SearchInputs
      listingTypes={listingTypes}
      selectedType={selectedType}
      onSearch={props.onSearch}
      query={query}
      searchLocation={location}
      setListingType={setListingType}
      setQuery={setUserQuery}
      clearSearchInput={clearSearchInput}
      redirectParam={redirectParam}
      setSearchLocation={setSearchLocation}
      search={search}
      placeholder={getText('Search by keyword, skill, or interest')}
      xsPlaceholder={getText('Keyword, skill, or interest')}
      inPageHeader={props.inPageHeader}
      clickLogin={clickLogin}
      clickSignup={clickSignup}
      showLogin={props.currentUser.isLoaded && !props.currentUser.id}
      currentUserLoaded={props.currentUser.isLoaded}
    />
  );
};

const mapStateToProps = ({ mainSearch, currentUser }) => ({
  mainSearch,
  initialized: mainSearch ? mainSearch.initialized : false,
  query: mainSearch ? mainSearch.query : '',
  searchMode: mainSearch ? mainSearch.searchMode : false,
  type: mainSearch && mainSearch.filters && mainSearch.filters.type,
  currentUser
});

export default withRouter(
  connect<Props, OwnProps, _, _, _, _>(
    mapStateToProps,
    // TODO: Figure out below, will solve most of our FlowFixMe statements
    // $FlowFixMe: for some reason, react-redux flow definition fails if we include the react-router Location type
    {
      setUserInitiatedSearch: setUserInitiatedSearchAction,
      setSearchFilter: setSearchFilterAction,
      setPageIndex: setPageIndexAction,
      setSearchQuery: setSearchQueryAction,
      setQueryKeywords: setQueryKeywordsAction,
      updateSearchLocation: updateSearchLocationAction,
      clearSearchLocation: clearSearchLocationAction,
      initializeSearch: initializeSearchAction,
      initializeLandingPageSearch: initializeLandingPageSearchAction,
      showModal: showModalAction
    }
  )(SearchInputsContainer)
);
